import { apiFetch } from "../../../services/apiFetch";
import {
  filterSubstituteCandidates,
  plannedSubstituteCandidate,
  substitutePickerQuery,
} from "../domain/substitution";

const WORKOUT_REQUEST_OPTIONS = Object.freeze({
  authRecovery: "none",
  csrfRecovery: "isolated",
});

export function startWorkoutExecution(payload, { signal } = {}) {
  return apiFetch("/api/workout-executions", {
    ...WORKOUT_REQUEST_OPTIONS,
    method: "POST",
    body: payload,
    ...(signal ? { signal } : {}),
  });
}

export function getWorkoutLogHub(timezone) {
  const query = timezone ? `?timezone=${encodeURIComponent(timezone)}` : "";
  return apiFetch(`/api/workout-log/hub${query}`, WORKOUT_REQUEST_OPTIONS);
}

function timezoneQuery(timezone) {
  return timezone ? `?timezone=${encodeURIComponent(timezone)}` : "";
}

// Choose Workout sources (Phase 7 checkpoint 7E, §A.3). Read-only.
export function getCurrentCycleSources(timezone) {
  return apiFetch(
    `/api/workout-log/sources/current-cycle${timezoneQuery(timezone)}`,
    WORKOUT_REQUEST_OPTIONS
  );
}

export function getPastCycleSources(timezone) {
  return apiFetch(
    `/api/workout-log/sources/past-cycles${timezoneQuery(timezone)}`,
    WORKOUT_REQUEST_OPTIONS
  );
}

export function getPastCycleSourceDetail(cycleId, timezone) {
  return apiFetch(
    `/api/workout-log/sources/past-cycles/${encodeURIComponent(cycleId)}${timezoneQuery(timezone)}`,
    WORKOUT_REQUEST_OPTIONS
  );
}

export function getWeeklyPlanSources() {
  return apiFetch("/api/workout-log/sources/weekly-plans", WORKOUT_REQUEST_OPTIONS);
}

export function getWorkoutLogExercisePicker(query = {}) {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    // A multi-select picker filter is one comma-separated parameter; an empty
    // selection sends nothing, so the default policy applies untouched.
    const serialized = Array.isArray(value) ? value.filter(Boolean).join(",") : value;
    if (serialized != null && serialized !== "") search.set(key, serialized);
  });
  const suffix = search.toString() ? `?${search.toString()}` : "";
  return apiFetch(
    `/api/workout-log/exercise-picker${suffix}`,
    WORKOUT_REQUEST_OPTIONS
  );
}

// Substitute-mode picker for one slot (§15.8). The query ranks against the
// slot's ORIGINAL exercise and filters by its snapshot class; the exercise the
// slot currently holds is removed client-side, keeping the server order.
export async function getWorkoutLogSubstitutePicker(record, item, {
  showAll = false,
  ...query
} = {}) {
  const response = await getWorkoutLogExercisePicker({
    ...query,
    ...substitutePickerQuery(record, item, { showAll }),
  });
  const items = response?.items || [];
  const filtered = filterSubstituteCandidates(record, item, items);
  // The slot's planned exercise leads the list while a replacement holds the
  // slot. The server never lists it, so the search text is matched here; under
  // a filter it is left out, since the snapshot cannot answer one.
  const planned = plannedSubstituteCandidate(record, item);
  const narrowed = Object.entries(query).some(([key, value]) =>
    !["q", "limit", "cursor", "executionId"].includes(key) &&
    (Array.isArray(value) ? value.length > 0 : value != null && value !== ""));
  const terms = String(query.q || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  const name = String(planned?.name || "").toLowerCase();
  const listPlanned = Boolean(planned) && !query.cursor && !narrowed &&
    terms.every((term) => name.includes(term));
  return {
    ...response,
    items: listPlanned ? [planned, ...filtered] : filtered,
    total: Math.max(0, Number(response?.total || 0) - (items.length - filtered.length)) +
      (listPlanned ? 1 : 0),
  };
}

// LAST / Suggested inputs for one added or substituted item (§13.1, A.3).
// Read-only; the caller treats any failure as "no guidance".
export function getWorkoutLogHistory(query = {}) {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && value !== "") search.set(key, value);
  });
  return apiFetch(
    `/api/workout-log/history?${search.toString()}`,
    WORKOUT_REQUEST_OPTIONS
  );
}

export function getWorkoutLogPreferences() {
  return apiFetch("/api/settings", WORKOUT_REQUEST_OPTIONS).then(
    (response) => response?.workoutPreferences || null
  );
}

export function getOpenWorkoutExecutions() {
  return apiFetch("/api/workout-executions/open", WORKOUT_REQUEST_OPTIONS);
}

export function getWorkoutExecution(executionId) {
  return apiFetch(
    `/api/workout-executions/${encodeURIComponent(executionId)}`,
    WORKOUT_REQUEST_OPTIONS
  );
}

export function takeoverWorkoutExecution(executionId, deviceId) {
  return apiFetch(
    `/api/workout-executions/${encodeURIComponent(executionId)}/takeover`,
    {
      ...WORKOUT_REQUEST_OPTIONS,
      method: "POST",
      body: { deviceId },
    }
  );
}

export function syncWorkoutExecution(executionId, payload) {
  return apiFetch(
    `/api/workout-executions/${encodeURIComponent(executionId)}`,
    {
      ...WORKOUT_REQUEST_OPTIONS,
      method: "PUT",
      body: payload,
    }
  );
}

export function patchWorkoutPreferences(patch) {
  return apiFetch("/api/settings/workout-preferences", {
    ...WORKOUT_REQUEST_OPTIONS,
    method: "PATCH",
    body: patch,
  });
}

export { WORKOUT_REQUEST_OPTIONS };
